import React, { ChangeEventHandler, FormEventHandler, useCallback, useEffect, useMemo, useState } from 'react';
import { Delete, Add } from '@material-ui/icons';
import { Button, Card, Grid, IconButton, Modal, Typography } from '@material-ui/core';
import { DragCard } from 'components/DragContainer/DragableCard';
import { IFormQuestionOption, IQuestionItem, isSelectType } from '../types';
import { useDragNDrop } from 'components/DragContainer/hook/useDragNDrop';
import { useStyles } from './styles';
import { useEditFormContext } from '../context';
import { WrappedTextfield } from 'components/Inputs/WrappedTextField';

type Props = {
  onUpdateQuestion: (data: IQuestionItem) => void;
};

export const EditQuestionModal = ({ onUpdateQuestion }: Props) => {
  const styles = useStyles();
  const { selectedQuestion, setSeletectedQuestion, questionList } = useEditFormContext();

  const [title, setTitle] = useState(selectedQuestion ? selectedQuestion.title : '');

  const isSelect = selectedQuestion ? isSelectType(selectedQuestion.type) : false;
  const [options, setOptions] = useState(selectedQuestion ? selectedQuestion.options : []);

  const [newOptionText, setNewOptionText] = useState('');

  const pos = useMemo(
    () => questionList.findIndex(({ id }) => id === selectedQuestion?.id) + 1,
    [selectedQuestion, questionList]
  );

  const clearModalData = () => {
    setTitle(selectedQuestion ? selectedQuestion.title : '');
    setOptions(selectedQuestion ? selectedQuestion.options : []);
    setNewOptionText('');
  };

  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (selectedQuestion) {
      setTitle(selectedQuestion.title);
      setOptions(selectedQuestion.options);
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }, [selectedQuestion]);

  const onCloseModal = () => {
    setIsOpen(false);
    setSeletectedQuestion(null);
    clearModalData();
  };

  const handleChangeOptionTitle: ChangeEventHandler<HTMLInputElement> = (event) => setNewOptionText(event.target.value);
  const handleAddOption: FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();

    if (!newOptionText) return;
    if (options.some((item) => item.title === newOptionText)) return;

    setOptions((prev) => [...prev, { index: prev.length, title: newOptionText }]);
    setNewOptionText('');
  };
  const handleDeleteOptionGenerator = (text: string) => () => {
    setOptions((prev) => prev.filter((item) => item.title !== text));
  };

  const handleConfirmChanges = () => {
    if (selectedQuestion) {
      onUpdateQuestion({ options, title, type: selectedQuestion.type, id: selectedQuestion.id });
      setIsOpen(false);
    }
  };

  const handleChangeTitle: ChangeEventHandler<HTMLInputElement> = (event) => setTitle(event.target.value);

  const handleChangeOptionOrder = useCallback(
    (data: IFormQuestionOption[]) => setOptions(data.map((item, index) => ({ ...item, index }))),
    []
  );
  const { handleDrop, handleLeave, handleOver, handleStart } = useDragNDrop({
    data: options,
    onChangeOrder: handleChangeOptionOrder
  });

  if (!selectedQuestion) return null;
  return (
    <Modal open={isOpen} onClose={onCloseModal} className={styles.modal}>
      <Grid
        component={Card}
        item
        xs={10}
        sm={8}
        md={6}
        style={{ padding: '16px' }}
        container
        direction="column"
        spacing={1}
      >
        <Typography align="center" variant="h6">
          Editando Questão {pos}
        </Typography>

        <Grid item>
          <WrappedTextfield
            label="Título da questão"
            name="title"
            value={title}
            onChange={handleChangeTitle}
            variant="outlined"
            fullWidth
            charLimit={100}
          />
        </Grid>

        {isSelect && (
          <>
            <Grid item onSubmit={handleAddOption} component="form" container>
              <Grid item xs={11}>
                <WrappedTextfield
                  name="optionTitle"
                  value={newOptionText}
                  onChange={handleChangeOptionTitle}
                  placeholder="Nova opção ..."
                  fullWidth
                  charLimit={4000}
                />
              </Grid>
              <Grid item xs={1}>
                <IconButton type="submit" aria-label="Adicionar opção" size="small">
                  <Add color="primary" />
                </IconButton>
              </Grid>
            </Grid>

            <Grid
              item
              component={Card}
              className={styles.optionsContainer}
              container
              direction="column"
              justifyContent="flex-start"
              alignItems="stretch"
            >
              {options.map((item, index) => (
                <DragCard
                  item={item}
                  pos={index + 1}
                  onDragStart={handleStart}
                  onDragLeave={handleLeave}
                  onDragOver={handleOver}
                  onDragDrop={handleDrop}
                  key={item.title}
                >
                  <Typography className={styles.title}>{item.title}</Typography>

                  <IconButton
                    type="button"
                    size="small"
                    aria-label="Deletar opção"
                    style={{ marginLeft: 'auto' }}
                    onClick={handleDeleteOptionGenerator(item.title)}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </DragCard>
              ))}
            </Grid>
          </>
        )}
        <Grid item>
          <Button fullWidth variant="contained" color="primary" onClick={handleConfirmChanges}>
            Atualizar
          </Button>
        </Grid>
      </Grid>
    </Modal>
  );
};
